import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "../common/config.js";
import LanguageSwitcher from "../common/LanguageSwitcher";

const request = async (path, method = 'GET', body = undefined) => {
  const response = await fetch(`${API_BASE_URL}/api/mailings/${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.msg || 'error');
  return data;
};

// Public page linked from every mailing: unsubscribe an address from all Risqui mailings
const UnsubscribePage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const token = searchParams.get('token') || '';
  const language = searchParams.get('language');

  const [state, setState] = useState('loading'); // loading | subscribed | unsubscribed | invalid | error
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (language === 'en' || language === 'nl') {
      i18n.changeLanguage(language);
    }
  }, [language, i18n]);

  useEffect(() => {
    if (!email || !token) {
      setState('invalid');
      return;
    }
    request(`unsubscribe?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`)
      .then((data) => setState(data.unsubscribed ? 'unsubscribed' : 'subscribed'))
      .catch(() => setState('invalid'));
  }, [email, token]);

  const update = async (path) => {
    setBusy(true);
    try {
      const data = await request(path, 'POST', { email, token });
      setState(data.unsubscribed ? 'unsubscribed' : 'resubscribed');
    } catch {
      setState('error');
    }
    setBusy(false);
  };

  return (
    <div id="login-container">
      <div className="container">
        <div className="col-md-12">
          <div id="login-content">
            <div id="login-logo">
              <i className="icon-risui" />
            </div>

            <div className="card card-container">
              <LanguageSwitcher />
              <h1>{t('unsubscribe_page.title')}</h1>
              <hr />

              {state === 'loading' && <p>{t('unsubscribe_page.loading')}</p>}

              {state === 'invalid' && (
                <div className="alert alert-danger" role="alert">{t('unsubscribe_page.invalid_link')}</div>
              )}

              {state === 'error' && (
                <div className="alert alert-danger" role="alert">{t('unsubscribe_page.error')}</div>
              )}

              {(state === 'subscribed' || state === 'resubscribed') && (
                <>
                  {state === 'resubscribed' && (
                    <div className="alert alert-success" role="alert">{t('unsubscribe_page.resubscribed')}</div>
                  )}
                  <p>{t('unsubscribe_page.confirm_text', { email })}</p>
                  <button type="button" className="btn btn-outline-primary btn-block login-submit" disabled={busy} onClick={() => update('unsubscribe')}>
                    {t('unsubscribe_page.unsubscribe_button')}
                  </button>
                </>
              )}

              {state === 'unsubscribed' && (
                <>
                  <div className="alert alert-success" role="alert">{t('unsubscribe_page.unsubscribed', { email })}</div>
                  <p style={{ fontSize: 13 }}>{t('unsubscribe_page.transactional_note')}</p>
                  <button type="button" className="btn btn-link" disabled={busy} onClick={() => update('resubscribe')}>
                    {t('unsubscribe_page.undo')}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnsubscribePage;
